import React, { useState, useEffect } from 'react';
import { colors } from './TabNavigation';
import ChartWheel from './ChartWheel';
import { clickable } from './a11y';

const API = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL.trim() : 'http://localhost:5001';

function ChartDisplay({ chart }) {
  const [expanded, setExpanded] = useState({
    angles: false,
    planets: false,
    additionalPoints: false,
    houses: false,
    aspects: false,
    elements: false
  });
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWheelInfo, setShowWheelInfo] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    // Coalesce bursts of resize events into one state update per frame.
    let raf = 0;
    const handle = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWindowWidth(window.innerWidth));
    };
    window.addEventListener('resize', handle);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', handle); };
  }, []);
  const isMobile = windowWidth < 700;

  // Keyboard users need a way to dismiss the modals (backdrop click is mouse-only).
  useEffect(() => {
    if (!interpretation && !showWheelInfo) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setInterpretation(null); setShowWheelInfo(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [interpretation, showWheelInfo]);

  // Welcome guide above the Big Three — reappears each new session, but a
  // dismiss sticks for the rest of that session (sessionStorage clears on close).
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return sessionStorage.getItem('astrela_seen_welcome') !== '1'; } catch { return true; }
  });

  const dismissWelcome = () => {
    setShowWelcome(false);
    try { sessionStorage.setItem('astrela_seen_welcome', '1'); } catch { /* private mode — fine */ }
  };

  if (!chart) return null;

  // Above the polar circle, quadrant systems (Placidus/Koch) are geometrically
  // undefined, so the backend falls back to Whole Sign and flags it. Surface that
  // honestly rather than presenting the result as the system the user picked.
  const polarHouses = chart.houses?.fellBack === true;

  const toggleSection = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchInterpretation = async (planet, sign) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/interpretation?planet=${planet}&sign=${sign}`);
      const data = response.ok ? await response.json() : null;
      setInterpretation(data && data.signInterpretation ? data : {
        planet, sign,
        signInterpretation: 'A written reading for this placement isn’t available yet.',
      });
    } catch (error) {
      console.error('Error fetching interpretation:', error);
      setInterpretation({ planet, sign, signInterpretation: 'Couldn’t load this reading just now — please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAspectInterpretation = async (aspect) => {
    setLoading(true);
    try {
      const p1 = aspect.planet1.key;
      const p2 = aspect.planet2.key;
      const aspectType = (aspect.aspect.name || '').toUpperCase();
      const response = await fetch(`${API}/api/aspect-interpretation?planet1=${p1}&planet2=${p2}&aspect=${aspectType}`);
      const data = response.ok ? await response.json() : null;
      // Always open the panel — with the reading, or a graceful note if this
      // pairing has no written interpretation yet (e.g. Vertex aspects).
      setInterpretation({
        type: 'aspect',
        planet1: `${aspect.planet1.symbol} ${aspect.planet1.name}`,
        planet2: `${aspect.planet2.symbol} ${aspect.planet2.name}`,
        aspectName: aspect.aspect.name,
        text: (data && data.interpretation) || 'A written reading for this pairing isn’t available yet.',
      });
    } catch (error) {
      console.error('Error fetching aspect interpretation:', error);
      setInterpretation({
        type: 'aspect',
        planet1: `${aspect.planet1.symbol} ${aspect.planet1.name}`,
        planet2: `${aspect.planet2.symbol} ${aspect.planet2.name}`,
        aspectName: aspect.aspect.name,
        text: 'Couldn’t load this reading just now — please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHouseInterpretation = async (houseNumber, sign) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/house-interpretation?house=${houseNumber}&sign=${sign}`);
      const data = response.ok ? await response.json() : null;
      setInterpretation({
        planet: `${getOrdinal(houseNumber)} House`,
        sign,
        signInterpretation: (data && data.interpretation) || 'A written reading for this placement isn’t available yet.',
      });
    } catch (error) {
      console.error('Error fetching house interpretation:', error);
      setInterpretation({
        planet: `${getOrdinal(houseNumber)} House`,
        sign,
        signInterpretation: 'Couldn’t load this reading just now — please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const closeInterpretation = () => setInterpretation(null);

  const getOrdinal = (num) => {
    const ordinals = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];
    return ordinals[num] || `${num}th`;
  };

  const getAspectColor = (aspectName) => {
    const name = (aspectName || '').toLowerCase();
    if (['opposition', 'square'].includes(name)) return '#c97070';
    if (['trine', 'sextile'].includes(name)) return '#6b9bc9';
    if (name === 'conjunction') return colors.gold;
    return colors.textSecondary;
  };

  const styles = {
    container: {
      maxWidth: '760px',
      margin: '0 auto',
      padding: '16px 24px 80px',
    },
    hero: {
      marginBottom: '72px',
      paddingBottom: '56px',
      borderBottom: `1px solid ${colors.borderSubtle}`,
      textAlign: 'center',
    },
    heroTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(24px, 7vw, 36px)',
      fontWeight: '400',
      color: colors.textPrimary,
      marginBottom: '12px',
      letterSpacing: '-0.3px',
      lineHeight: '1.2',
    },
    heroSubtitle: {
      fontSize: isMobile ? '16px' : '14px',
      fontStyle: 'italic',
      fontWeight: '300',
      color: colors.textSecondary,
      lineHeight: '1.8',
    },
    section: {
      marginBottom: '72px',
    },
    sectionTitle: {
      fontSize: isMobile ? '12px' : '10px',
      fontFamily: 'inherit',
      fontWeight: '500',
      color: colors.gold,
      textTransform: 'uppercase',
      letterSpacing: '2.5px',
      marginBottom: '28px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '44px',
      paddingBottom: '16px',
      borderBottom: `1px solid ${colors.borderSubtle}`,
      transition: 'color 0.2s',
    },
    bigThreeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1px',
      backgroundColor: colors.borderSubtle,
      marginBottom: '0',
    },
    card: {
      backgroundColor: colors.darkSurface,
      padding: '28px 24px',
      cursor: 'pointer',
      transition: 'background-color 0.25s ease',
    },
    cardLabel: {
      fontSize: isMobile ? '12px' : '10px',
      fontFamily: 'inherit',
      fontWeight: '500',
      color: colors.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '2px',
      marginBottom: '12px',
    },
    cardValue: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '22px',
      fontWeight: '400',
      color: colors.textPrimary,
      marginBottom: '6px',
      lineHeight: '1.2',
    },
    cardPosition: {
      fontSize: isMobile ? '15px' : '13px',
      color: colors.gold,
      fontWeight: '300',
    },
    list: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    listItem: {
      padding: '18px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    planetName: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: isMobile ? '17px' : '16px',
      color: colors.textPrimary,
      fontWeight: '400',
      flex: 1,
    },
    planetPosition: {
      fontSize: isMobile ? '15px' : '13px',
      color: colors.textSecondary,
      fontWeight: '300',
      textAlign: 'right',
    },
    aspectRow: {
      padding: '14px 0',
      borderBottom: `1px solid ${colors.borderSubtle}`,
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr auto',
      gap: '12px',
      alignItems: 'center',
      fontSize: '14px',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px',
      backdropFilter: 'blur(4px)',
    },
    modalContent: {
      backgroundColor: '#ffffff',
      border: `1px solid ${colors.borderSubtle}`,
      boxShadow: '0 24px 64px rgba(26, 20, 16, 0.14)',
      padding: isMobile ? '24px' : '48px',
      maxWidth: isMobile ? '95vw' : '560px',
      width: '100%',
      maxHeight: '80vh',
      overflowY: 'auto',
      position: 'relative',
    },
    description: {
      fontSize: isMobile ? '15px' : '13px',
      color: colors.textSecondary,
      fontWeight: '300',
      lineHeight: '1.7',
      marginBottom: '24px',
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>

      {/* Birth Chart Wheel */}
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <ChartWheel chart={chart} size={340} />
        <button
          onClick={() => setShowWheelInfo(true)}
          title="How to read this chart"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: `1px solid ${colors.borderGold}`,
            backgroundColor: 'transparent',
            color: colors.gold,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'inherit',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(201,149,110,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ⓘ
        </button>
      </div>

      {/* Wheel info overlay */}
      {showWheelInfo && (
        <div
          style={styles.modal}
          onClick={() => setShowWheelInfo(false)}
        >
          <div style={{ ...styles.modalContent, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowWheelInfo(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '24px',
                background: 'none',
                border: 'none',
                fontSize: '22px',
                cursor: 'pointer',
                color: colors.textMuted,
                padding: '4px',
                lineHeight: 1,
                fontFamily: 'inherit',
              }}
            >
              ×
            </button>
            <h3 style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '24px',
              fontWeight: '400',
              color: colors.textPrimary,
              marginBottom: '24px',
              marginTop: 0,
            }}>
              How to read your chart
            </h3>
            {[
              {
                label: 'Outer ring — The Zodiac',
                text: 'The 12 zodiac signs form the backdrop of the sky. They describe the style or flavour of energy — not what happens, but how it feels.',
              },
              {
                label: 'Inner sections — The Houses',
                text: 'The wheel is divided into 12 houses, each representing a life area: identity, money, communication, home, love, work, relationships, and more. Roman numerals (I–XII) mark each one.',
              },
              {
                label: 'Symbols — The Planets',
                text: 'Each symbol on the wheel is a planet. Planets represent different parts of you — the Sun is your core self, the Moon is your emotions, Venus is love and beauty, Mars is drive and desire, and so on.',
              },
              {
                label: 'Lines in the centre — Aspects',
                text: 'The lines connecting planets show how they interact. Blue lines mean harmony or flow. Red lines mean tension or challenge — not bad, just where growth happens.',
              },
              {
                label: 'AC / DC / MC / IC — The Axes',
                text: 'The four anchor points. AC (Ascendant) is your Rising Sign — how you come across to others. DC is relationships. MC is career and public life. IC is home and roots.',
              },
            ].map(({ label, text }) => (
              <div key={label} style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: isMobile ? '13px' : '11px',
                  fontWeight: '500',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: colors.gold,
                  marginBottom: '6px',
                }}>
                  {label}
                </div>
                <p style={{
                  fontSize: isMobile ? '16px' : '14px',
                  lineHeight: '1.75',
                  color: colors.textSecondary,
                  fontWeight: '300',
                  margin: 0,
                }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>Your Birth Chart</h1>
        <p style={styles.heroSubtitle}>
          {chart.birthData.date} &nbsp;&middot;&nbsp; {chart.birthData.time}<br />
          {chart.birthData.latitude?.toFixed(2)}°, {chart.birthData.longitude?.toFixed(2)}°
        </p>
      </div>

      {/* First-run welcome — points the way before the dense sections */}
      {showWelcome && (
        <div style={{
          position: 'relative',
          maxWidth: '560px',
          margin: '0 auto 48px',
          padding: isMobile ? '20px 22px' : '24px 28px',
          backgroundColor: 'rgba(201, 149, 110, 0.07)',
          border: `1px solid ${colors.borderGold}`,
          borderRadius: '2px',
        }}>
          <button
            onClick={dismissWelcome}
            aria-label="Dismiss"
            style={{
              position: 'absolute',
              top: '8px',
              right: '10px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: colors.textMuted,
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px',
              lineHeight: 1,
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
          <div style={{
            fontSize: isMobile ? '13px' : '11px',
            fontWeight: '500',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: colors.gold,
            marginBottom: '10px',
          }}>
            Welcome to your chart
          </div>
          <p style={{
            fontSize: isMobile ? '17px' : '15px',
            lineHeight: '1.7',
            color: colors.textSecondary,
            fontWeight: '300',
            margin: 0,
            paddingRight: '24px',
          }}>
            This is your cosmic map — there's a lot here, so take it slow. Start with your{' '}
            <strong style={{ color: colors.textPrimary, fontWeight: '400' }}>Big Three</strong> just below:
            your Rising, Sun, and Moon signs. <strong style={{ color: colors.textPrimary, fontWeight: '400' }}>Tap any
            card, planet, or aspect</strong> to read what it means for you. New to charts? The{' '}
            <span style={{ color: colors.gold }}>ⓘ</span> by the wheel explains how to read it.
          </p>
        </div>
      )}

      {/* The Big Three */}
      <div style={styles.section}>
        <div style={{ ...styles.sectionTitle, cursor: 'default' }}>
          <span>The Big Three</span>
        </div>
        <div style={styles.bigThreeGrid}>
          {[
            { label: 'Rising Sign', tagline: 'How others first see you', value: chart.houses.ascendant.sign, symbol: chart.houses.ascendant.symbol, position: chart.houses.ascendant.formatted, onClick: () => fetchInterpretation('Ascendant', chart.houses.ascendant.sign) },
            { label: 'Sun Sign', tagline: 'Your core identity', value: chart.planets.SUN.sign, symbol: chart.planets.SUN.symbol, position: chart.planets.SUN.formatted, onClick: () => fetchInterpretation('Sun', chart.planets.SUN.sign) },
            { label: 'Moon Sign', tagline: 'Your emotional world', value: chart.planets.MOON.sign, symbol: chart.planets.MOON.symbol, position: chart.planets.MOON.formatted, onClick: () => fetchInterpretation('Moon', chart.planets.MOON.sign) },
          ].map(({ label, tagline, value, symbol, position, onClick }) => (
            <div
              key={label}
              style={styles.card}
              {...clickable(onClick)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurfaceHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
            >
              <div style={styles.cardLabel}>{label}</div>
              <div style={styles.cardValue}>{symbol} {value}</div>
              <div style={{ fontSize: isMobile ? '14px' : '12px', color: colors.textMuted, fontStyle: 'italic', fontWeight: '300', marginTop: '4px', marginBottom: '10px' }}>{tagline}</div>
              <div style={{ ...styles.cardPosition, fontSize: isMobile ? '13px' : '11px' }}>{position} · tap to explore</div>
            </div>
          ))}
        </div>
      </div>

      {/* Angles */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('angles'), { expanded: expanded.angles })}>
          <span>Angles</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.angles ? '−' : '+'}</span>
        </div>
        {expanded.angles && (
          <>
            <p style={styles.description}>The four most sensitive and anchoring points in your birth chart representing key life pillars such as self, relationships, career, and home.</p>
            <ul style={styles.list}>
              {[
                { label: 'Ascendant', name: 'ASC', value: chart.houses.ascendant, onClick: () => fetchInterpretation('Ascendant', chart.houses.ascendant.sign) },
                { label: 'Descendant', name: 'DSC', value: chart.houses.descendant, onClick: () => fetchInterpretation('Descendant', chart.houses.descendant.sign) },
                { label: 'Midheaven', name: 'MC', value: chart.houses.mc, onClick: () => fetchInterpretation('Midheaven', chart.houses.mc.sign) },
                { label: 'Imum Coeli', name: 'IC', value: chart.houses.ic, onClick: () => fetchInterpretation('Imum Coeli', chart.houses.ic.sign) },
              ].map(({ label, name, value, onClick }) => (
                <li
                  key={name}
                  style={styles.listItem}
                  {...clickable(onClick)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>
                    {value.symbol} {label}
                  </span>
                  <span style={styles.planetPosition}>
                    {value.sign}&nbsp;&nbsp;{value.formatted}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Planets */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('planets'), { expanded: expanded.planets })}>
          <span>Planets</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.planets ? '−' : '+'}</span>
        </div>
        {expanded.planets && (
          <>
            <p style={styles.description}>Your core drives and traits, showing what energies influence your personality, potential and challenges.</p>
            <ul style={styles.list}>
            {['SUN','MOON','MERCURY','VENUS','MARS','JUPITER','SATURN','URANUS','NEPTUNE','PLUTO'].map(key => {
              const planet = chart.planets[key];
              return (
                <li
                  key={key}
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation(planet.name, planet.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>
                    {planet.symbol} {planet.name}
                    {planet.longitudeSpeed < 0 && (
                      <span style={{ color: colors.twilightPurple, marginLeft: '8px', fontSize: '13px' }}>℞</span>
                    )}
                  </span>
                  <span style={styles.planetPosition}>
                    {planet.sign}&nbsp;&nbsp;{planet.formatted}
                  </span>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>

      {/* Additional Points */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('additionalPoints'), { expanded: expanded.additionalPoints })}>
          <span>Additional Points</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.additionalPoints ? '−' : '+'}</span>
        </div>
        {expanded.additionalPoints && (
          <>
            <p style={styles.description}>Subtle influences that reveal deeper patterns.</p>
            <ul style={styles.list}>
              {chart.planets.CHIRON && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('Chiron', chart.planets.CHIRON.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{chart.planets.CHIRON.symbol} Chiron</span>
                  <span style={styles.planetPosition}>{chart.planets.CHIRON.sign}&nbsp;&nbsp;{chart.planets.CHIRON.formatted}</span>
                </li>
              )}
              {chart.planets.NORTH_NODE && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('North Node', chart.planets.NORTH_NODE.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{chart.planets.NORTH_NODE.symbol} North Node</span>
                  <span style={styles.planetPosition}>{chart.planets.NORTH_NODE.sign}&nbsp;&nbsp;{chart.planets.NORTH_NODE.formatted}</span>
                </li>
              )}
              {chart.planets.SOUTH_NODE && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('South Node', chart.planets.SOUTH_NODE.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{chart.planets.SOUTH_NODE.symbol} South Node</span>
                  <span style={styles.planetPosition}>{chart.planets.SOUTH_NODE.sign}&nbsp;&nbsp;{chart.planets.SOUTH_NODE.formatted}</span>
                </li>
              )}
              {chart.planets.LILITH_MEAN && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('Lilith', chart.planets.LILITH_MEAN.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{chart.planets.LILITH_MEAN.symbol} Lilith</span>
                  <span style={styles.planetPosition}>{chart.planets.LILITH_MEAN.sign}&nbsp;&nbsp;{chart.planets.LILITH_MEAN.formatted}</span>
                </li>
              )}
              {chart.planets.PART_OF_FORTUNE && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('Part of Fortune', chart.planets.PART_OF_FORTUNE.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{chart.planets.PART_OF_FORTUNE.symbol} Part of Fortune</span>
                  <span style={styles.planetPosition}>{chart.planets.PART_OF_FORTUNE.sign}&nbsp;&nbsp;{chart.planets.PART_OF_FORTUNE.formatted}</span>
                </li>
              )}
              {chart.houses && chart.houses.vertex && (
                <li
                  style={styles.listItem}
                  {...clickable(() => fetchInterpretation('Vertex', chart.houses.vertex.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>Vx Vertex</span>
                  <span style={styles.planetPosition}>{chart.houses.vertex.sign}&nbsp;&nbsp;{chart.houses.vertex.formatted}</span>
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      {/* Houses */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('houses'), { expanded: expanded.houses })}>
          <span>Houses</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.houses ? '−' : '+'}</span>
        </div>
        {expanded.houses && (
          <>
            <p style={styles.description}>The 12 houses represent different life areas. Each house cusp (starting point) falls in a zodiac sign, coloring how you experience that life area.</p>
            {polarHouses && (
              <p style={{ ...styles.description, color: colors.gold, fontStyle: 'italic' }}>
                Note: you were born inside the polar circle, where the standard Placidus/Koch way of dividing the houses isn’t mathematically defined. Your houses use Whole Sign instead — your planet signs, Ascendant, Midheaven, and aspects are unaffected.
              </p>
            )}
            <ul style={styles.list}>
            {(chart.houses.houses || chart.houses.cusps || []).map((house, idx) => {
              const num = house.house || idx + 1;
              return (
                <li
                  key={num}
                  style={styles.listItem}
                  {...clickable(() => fetchHouseInterpretation(num, house.sign))}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={styles.planetName}>{getOrdinal(num)} House</span>
                  <span style={styles.planetPosition}>{house.symbol} {house.sign}&nbsp;&nbsp;{house.formatted}</span>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>

      {/* Aspects */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('aspects'), { expanded: expanded.aspects })}>
          <span>Aspects</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.aspects ? '−' : '+'}</span>
        </div>
        {expanded.aspects && (
          <>
            <p style={styles.description}>Shows how different planets interact — whether they create harmony (blue), tension (red) or opportunity (gold).</p>
            {(() => {
          const planetKeys = ['SUN','MOON','MERCURY','VENUS','MARS','JUPITER','SATURN','URANUS','NEPTUNE','PLUTO'];
          const planetAspects = chart.aspects.filter(a => planetKeys.includes(a.planet1.key) && planetKeys.includes(a.planet2.key));
          const otherAspects = chart.aspects.filter(a => !planetKeys.includes(a.planet1.key) || !planetKeys.includes(a.planet2.key));
          const renderAspects = (list) => list.map((aspect, i) => {
            const isTight = parseFloat(aspect.orb) < 1;
            const aspectColor = getAspectColor(aspect.aspect.name);
            return (
              <div
                key={i}
                style={{
                  ...styles.aspectRow,
                  backgroundColor: isTight ? colors.violetDim : 'transparent',
                  paddingLeft: isTight ? '12px' : '0',
                  paddingRight: isTight ? '12px' : '0',
                  cursor: 'pointer',
                }}
                {...clickable(() => fetchAspectInterpretation(aspect))}
                onMouseEnter={(e) => { if (!isTight) e.currentTarget.style.backgroundColor = colors.darkSurface; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isTight ? colors.violetDim : 'transparent'; }}
              >
                <span style={{ color: colors.textPrimary, textAlign: 'right', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: isMobile ? '16px' : '15px' }}>
                  {aspect.planet1.symbol} {aspect.planet1.name}
                </span>
                <span style={{ color: aspectColor, fontSize: isMobile ? '13px' : '12px', fontWeight: '400', letterSpacing: '0.5px', textAlign: 'center', minWidth: '64px' }}>
                  {aspect.aspect.name}
                </span>
                <span style={{ color: colors.textPrimary, fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: isMobile ? '16px' : '15px' }}>
                  {aspect.planet2.symbol} {aspect.planet2.name}
                </span>
                <span style={{
                  color: isTight ? colors.twilightPurple : colors.textMuted,
                  fontSize: isMobile ? '13px' : '12px',
                  textAlign: 'right',
                  fontWeight: '300',
                  whiteSpace: 'nowrap',
                }}>
                  {isTight && '· '}{aspect.orb}°
                </span>
              </div>
            );
          });

          return (
            <>
              {planetAspects.length > 0 && (
                <>
                  <div style={{ fontSize: isMobile ? '12px' : '10px', letterSpacing: '2px', textTransform: 'uppercase', color: colors.textMuted, marginBottom: '16px' }}>
                    Planet Aspects
                  </div>
                  {renderAspects(planetAspects)}
                </>
              )}
              {otherAspects.length > 0 && (
                <>
                  <div style={{ fontSize: isMobile ? '12px' : '10px', letterSpacing: '2px', textTransform: 'uppercase', color: colors.textMuted, marginTop: '40px', marginBottom: '16px' }}>
                    Additional Aspects
                  </div>
                  {renderAspects(otherAspects)}
                </>
              )}
            </>
          );
            })()}
          </>
        )}
      </div>

      {/* Elements & Modalities */}
      <div style={styles.section}>
        <div style={styles.sectionTitle} {...clickable(() => toggleSection('elements'), { expanded: expanded.elements })}>
          <span>Elements & Modalities</span>
          <span style={{ fontSize: '18px', fontWeight: '200', color: colors.textMuted }}>{expanded.elements ? '−' : '+'}</span>
        </div>
        {expanded.elements && (chart.elements || chart.modalities) && (
          <>
            <p style={styles.description}>Elements show your energy style (Fire = action, Earth = practical, Air = mental, Water = emotional). Modalities show your approach (Cardinal = initiating, Fixed = sustaining, Mutable = adapting).</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
            {chart.elements && (
              <div>
                <div style={{ fontSize: isMobile ? '12px' : '10px', letterSpacing: '2px', textTransform: 'uppercase', color: colors.textMuted, marginBottom: '16px' }}>
                  Elements
                </div>
                <ul style={styles.list}>
                  {Object.entries(chart.elements).map(([element, count]) => (
                    <li key={element} style={{ ...styles.listItem, cursor: 'default' }}>
                      <span style={styles.planetName}>{element}</span>
                      <span style={{ ...styles.planetPosition, color: colors.gold }}>{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {chart.modalities && (
              <div>
                <div style={{ fontSize: isMobile ? '12px' : '10px', letterSpacing: '2px', textTransform: 'uppercase', color: colors.textMuted, marginBottom: '16px' }}>
                  Modalities
                </div>
                <ul style={styles.list}>
                  {Object.entries(chart.modalities).map(([modality, count]) => (
                    <li key={modality} style={{ ...styles.listItem, cursor: 'default' }}>
                      <span style={styles.planetName}>{modality}</span>
                      <span style={{ ...styles.planetPosition, color: colors.gold }}>{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {/* Interpretation Modal */}
      {interpretation && (
        <div style={styles.modal} onClick={closeInterpretation}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeInterpretation}
              style={{
                position: 'absolute',
                top: '20px',
                right: '24px',
                background: 'none',
                border: 'none',
                fontSize: '22px',
                cursor: 'pointer',
                color: colors.textMuted,
                padding: '4px',
                lineHeight: 1,
                fontFamily: 'inherit',
              }}
            >
              ×
            </button>
            {interpretation.type === 'aspect' ? (
              <>
                <h3 style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: '26px',
                  fontWeight: '400',
                  color: colors.textPrimary,
                  marginBottom: '4px',
                  marginTop: 0,
                  lineHeight: '1.2',
                }}>
                  {interpretation.planet1} {interpretation.aspectName} {interpretation.planet2}
                </h3>
                <p style={{
                  fontSize: isMobile ? '14px' : '12px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: getAspectColor(interpretation.aspectName),
                  marginBottom: '20px',
                  marginTop: '6px',
                  fontWeight: '400',
                }}>
                  {interpretation.aspectName}
                </p>
                <p style={{
                  fontSize: isMobile ? '17px' : '15px',
                  lineHeight: '1.85',
                  color: colors.textSecondary,
                  fontWeight: '300',
                  margin: 0,
                }}>
                  {interpretation.text}
                </p>
              </>
            ) : (
              <>
                <h3 style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: '26px',
                  fontWeight: '400',
                  color: colors.textPrimary,
                  marginBottom: '8px',
                  marginTop: 0,
                  lineHeight: '1.2',
                }}>
                  {interpretation.planet} in {interpretation.sign}
                </h3>
                <p style={{
                  fontSize: isMobile ? '17px' : '15px',
                  lineHeight: '1.85',
                  color: colors.textSecondary,
                  fontWeight: '300',
                  margin: 0,
                }}>
                  {interpretation.signInterpretation}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#ffffff',
          border: `1px solid ${colors.borderSubtle}`,
          boxShadow: '0 8px 32px rgba(26, 20, 16, 0.1)',
          padding: '24px 40px',
          fontSize: isMobile ? '15px' : '13px',
          fontStyle: 'italic',
          color: colors.textSecondary,
          letterSpacing: '0.5px',
        }}>
          loading&hellip;
        </div>
      )}
    </div>
  );
}

export default ChartDisplay;
